import { Box } from '@everlywell/ui-kit';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import analytics from 'utils/analytics';
import { QuestLocation } from 'utils/api/questApis';
import { ANALYTICS } from 'utils/constants/analytics';

import PatientServiceCenterLocation from './components/PatientServiceCenterLocation';
import QuestLocationFormAndList from './components/QuestLocationFormAndList';
import { ANIMATION_DURATION } from './QuestLocationFinder.constants';

export type QuestLocationFinderProps = {
  backgroundImages?: Queries.pdpHeroFragment['images'];
};

/**
 * [Quest Location Finder for Quest Product PDP].
 */
const QuestLocationFinder = (props: QuestLocationFinderProps) => {
  const { backgroundImages } = props;

  const [selectedLocation, setSelectedLocation] =
    useState<QuestLocation | null>(null);

  const [widgetState, setWidgetState] = useState<
    'landing' | 'list' | 'listItem'
  >('landing');

  const goToListView = () => {
    setWidgetState('list');
  };

  const goToSpecificLocationView = (location: QuestLocation) => {
    setWidgetState('listItem');
    setSelectedLocation(location);
  };

  const handleLocationClick = (location: QuestLocation) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: 'PSC location',
      },
    });
    goToSpecificLocationView(location);
  };

  const handleBackClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        label: 'Back from PSC details',
      },
    });
    goToListView();
  };

  useEffect(() => {
    const label = {
      landing: 'Location Finder Start Screen',
      list: 'Location Finder Results',
      listItem: 'PSC Details',
    }[widgetState];

    analytics.track({
      event: ANALYTICS.EVENTS.VIEWED_COMPONENT,
      data: {
        label,
      },
    });
  }, [widgetState]);

  const questLandingImage = backgroundImages?.find(
    (image) =>
      image?.desktopImageAltText?.toLowerCase().includes('landing') ||
      image?.mobileImageAltText?.toLowerCase().includes('landing'),
  );

  const questSearchImage = backgroundImages?.find(
    (image) =>
      image?.desktopImageAltText?.toLowerCase().includes('search') ||
      image?.mobileImageAltText?.toLowerCase().includes('search'),
  );

  return (
    <Box
      as={motion.div}
      backgroundColor={'moonlight.wash'}
      padding={[4, 10]}
      borderRadius={'12px'}
      marginTop={[6, null, 8]}
      minHeight="400px"
      boxSizing="content-box"
      backgroundImage={[
        `url(${questLandingImage?.mobileImage?.gatsbyImageData?.images.fallback?.src})`,
        `url(${questLandingImage?.desktopImage?.gatsbyImageData?.images.fallback?.src})`,
      ]}
      backgroundPosition={'bottom right'}
      backgroundSize={'cover'}
      backgroundRepeat={'no-repeat'}
      display={'flex'}
      position="relative"
      overflow="clip"
    >
      <Box
        as={motion.div}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: [
            `url(${questSearchImage?.mobileImage?.gatsbyImageData?.images.fallback?.src})`,
            `url(${questSearchImage?.desktopImage?.gatsbyImageData?.images.fallback?.src})`,
          ],
          backgroundPosition: 'bottom right',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
        variants={{
          landing: { opacity: 0 },
          list: {
            opacity: 1,
            transition: {
              type: 'tween',
              duration: ANIMATION_DURATION,
            },
          },
          listItem: { opacity: 1 },
        }}
        initial={'landing'}
        animate={widgetState}
      />
      <Box sx={{ position: 'relative', width: '100%' }}>
        <QuestLocationFormAndList
          onSearchSubmit={goToListView}
          onLocationClick={handleLocationClick}
          widgetState={widgetState}
        />
        <PatientServiceCenterLocation
          selectedLocation={selectedLocation}
          handleBackClick={handleBackClick}
          widgetState={widgetState}
        />
      </Box>
    </Box>
  );
};

export default QuestLocationFinder;
